import { useMemo, useState } from "react";
import { brDate } from "../../format";
import { fmtNum, fmtPct } from "../../utils/formatters";
import {
  mockFaMinhasContrapartes,
  mockFaRankingCcee,
  mockFaSemanas,
} from "../../utils/mockDataMesa";
import { DataSourceBadge } from "../ui/DataSourceBadge";
import { FaBadge } from "./FaBadge";
import { MetricChip } from "./MetricChip";
import { Sparkline } from "./Sparkline";

export function FaTab() {
  const [semana, setSemana] = useState(mockFaSemanas[0]);

  const minhasSiglasUpper = useMemo(
    () => new Set(mockFaMinhasContrapartes.map((m) => m.sigla.toUpperCase())),
    [],
  );

  const faAtual = mockFaMinhasContrapartes.map((c) => c.faHist[0]);
  const faMedio = faAtual.reduce((s, v) => s + v, 0) / (faAtual.length || 1);
  const count05 = mockFaMinhasContrapartes.filter((c) => c.faHist[0] > 0.5).length;

  const maiorMinha = mockFaMinhasContrapartes.reduce(
    (max, c) => (c.faHist[0] > max.faHist[0] ? c : max),
    mockFaMinhasContrapartes[0],
  );
  const maiorMercado = mockFaRankingCcee.reduce(
    (max, c) => (c.fa > max.fa ? c : max),
    mockFaRankingCcee[0],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-black text-white font-sans">
              Fator de Ajuste (FA) — CCEE
            </h2>
            {/* Fail-closed 02/08: aba ainda 100% mock (#247) — rotular, nunca
                exibir dado de exemplo com cara de real. */}
            <DataSourceBadge
              label="dados de exemplo"
              title="Aba em protótipo (#247): dados de exemplo de set/2025 — integração CCEE (FA) ainda não existe"
            />
          </div>
          <p className="text-white/50 text-sm mt-1">
            Layout de referência com dados de exemplo (set/2025) — integração CCEE pendente (#247)
          </p>
        </div>
        <select
          value={semana}
          onChange={(e) => setSemana(e.target.value)}
          className="bg-indra-azul-deep border border-white/10 rounded-md px-3 py-1.5 text-sm text-white font-mono"
        >
          {mockFaSemanas.map((s) => (
            <option key={s} value={s}>
              {brDate(s)}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MetricChip label="FA médio (minhas)" value={fmtNum(faMedio, 4)} />
        <MetricChip
          label="Contrapartes FA > 0,5"
          value={`${count05} / ${mockFaMinhasContrapartes.length}`}
        />
        <MetricChip
          label="Maior FA minhas"
          value={`${fmtNum(maiorMinha.faHist[0], 4)} · ${maiorMinha.sigla}`}
        />
        <MetricChip
          label="Maior FA mercado"
          value={`${fmtNum(maiorMercado.fa, 2)} · ${maiorMercado.sigla}`}
        />
      </div>

      <div className="bg-indra-azul-surface rounded-lg border border-white/10 p-4">
        <h3 className="text-white font-semibold text-sm mb-3">Minhas Contrapartes</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Sigla</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">FA atual</th>
                <th className="text-center py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Histórico</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Δ vs anterior</th>
                <th className="text-center py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Zona</th>
              </tr>
            </thead>
            <tbody>
              {mockFaMinhasContrapartes.map((c, i) => {
                const atual = c.faHist[0];
                const anterior = c.faHist[1];
                const delta = anterior === 0 ? 0 : ((atual - anterior) / anterior) * 100;
                const serieCrono = [...c.faHist].reverse();
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-3 text-sm text-white font-medium">{c.sigla}</td>
                    <td className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white">
                      {fmtNum(atual, 4)}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex justify-center">
                        <Sparkline data={serieCrono} />
                      </div>
                    </td>
                    <td
                      className={
                        "py-2 px-3 text-right text-sm font-mono tabular-nums " +
                        (delta > 0 ? "text-red-400" : delta < 0 ? "text-indra-turquesa" : "text-white/40")
                      }
                    >
                      {delta > 0 ? "+" : ""}
                      {fmtPct(delta)}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <FaBadge fa={atual} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-indra-azul-surface rounded-lg border border-white/10 p-4">
        <h3 className="text-white font-semibold text-sm mb-3">
          Ranking CCEE · top 30 FA — semana {brDate(semana)}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">#</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Sigla</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Classe</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Cód. Evento</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">FA</th>
              </tr>
            </thead>
            <tbody>
              {mockFaRankingCcee.slice(0, 30).map((r, i) => {
                const destaque = minhasSiglasUpper.has(r.sigla.toUpperCase());
                return (
                  <tr
                    key={i}
                    className={
                      "border-b border-white/5 hover:bg-white/5 " +
                      (destaque ? "bg-indra-turquesa/10 border-l-2 border-indra-turquesa" : "")
                    }
                  >
                    <td className="py-2 px-3 text-sm text-white/50 font-mono">{i + 1}</td>
                    <td className={"py-2 px-3 text-sm font-medium " + (destaque ? "text-indra-turquesa" : "text-white")}>
                      {r.sigla}
                    </td>
                    <td className="py-2 px-3 text-xs text-white/60">{r.classe}</td>
                    <td className="py-2 px-3 text-right text-xs font-mono tabular-nums text-white/60">
                      {r.codEvento}
                    </td>
                    <td className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white">
                      {fmtNum(r.fa, 2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-white/40 leading-relaxed max-w-3xl">
        Zonas de FA: ≤0,30 verde (saudável), 0,30-0,50 amarelo (atenção), &gt;0,50 vermelho
        (risco — contraparte com desvio acentuado entre medição física e liquidação).
      </p>
    </div>
  );
}
